import {assign, setup, sendTo} from 'xstate';

import {formAutofillCo2ClassAction} from './actions/formAutofillCo2ClassAction';
import {formCalculateFluidNextReplacementDateFromLastDateAction} from './actions/formCalculateFluidNextReplacementDateFromLastDateAction';
import {formCalculateFluidNextReplacementDateFromMonthsAction} from './actions/formCalculateFluidNextReplacementDateFromMonthsAction';
import {formCalculateNextReplacementDateAction} from './actions/formCalculateNextReplacementDateAction';
import {formCalculateNextReplacementDateIntervalAction} from './actions/formCalculateNextReplacementDateIntervalAction';
import {formCalculateNextReplacementMileageAction} from './actions/formCalculateNextReplacementMileageAction';
import {formCalculateNextReplacementMileageIntervalAction} from './actions/formCalculateNextReplacementMileageIntervalAction';
import {formCalculateServiceInspectionDateAction} from './actions/formCalculateServiceInspectionDateAction';
import {formCalculateServiceInspectionDateIntervalAction} from './actions/formCalculateServiceInspectionDateIntervalAction';
import {formCalculateServiceInspectionMileageAction} from './actions/formCalculateServiceInspectionMileageAction';
import {formCalculateServiceInspectionMileageIntervalAction} from './actions/formCalculateServiceInspectionMileageIntervalAction';
import {formClearBatteryHealthFieldsAction} from './actions/formClearBatteryHealthFieldsAction';
import {formClearPhevFieldsAction} from './actions/formClearPhevFieldsAction';
import {formCreateCustomMakeAction} from './actions/formCreateCustomMakeAction';
import {formCreateCustomModelAction} from './actions/formCreateCustomModelAction';
import {formResetAction} from './actions/formResetAction';
import {formResetOnTypeChangeAction} from './actions/formResetOnTypeChangeAction';
import {formUpdateFeaturesAction} from './actions/formUpdateFeaturesAction';
import {formUpdateFromVinAction} from './actions/formUpdateFromVinAction';
import {formUpdatePhotosAction} from './actions/formUpdatePhotosAction';
import {formUpdateProductionYearAction} from './actions/formUpdateProductionYearAction';
import {fieldWatchersActors} from './actors/fieldWatchersActors';
import {VEHICLE_FORM_EVENTS} from './constants/VEHICLE_FORM_EVENTS';
import {StartWatchingEvent, VehicleMachineEvents} from './types/events';
import {VehicleMachineContext} from './types/VehicleMachineContext';

const VIN_DECODE_ERROR_TIMEOUT_MS = 5000;

export const machine = setup({
  types: {
    context: {} as VehicleMachineContext,
    events: {} as VehicleMachineEvents,
  },
  actors: {
    // Field watchers
    typeWatcher: fieldWatchersActors.typeWatcherActor,
    makeWatcher: fieldWatchersActors.makeWatcherActor,
    modelWatcher: fieldWatchersActors.modelWatcherActor,
    fuelTypeWatcher: fieldWatchersActors.fuelTypeWatcherActor,
  },
  actions: {
    formReset: formResetAction,
    formUpdateFromVin: formUpdateFromVinAction,
    formCreateCustomMake: formCreateCustomMakeAction,
    formCreateCustomModel: formCreateCustomModelAction,
    formUpdateProductionYear: formUpdateProductionYearAction,
    formUpdatePhotos: formUpdatePhotosAction,
    formUpdateFeatures: formUpdateFeaturesAction,
    formClearBatteryHealthFields: formClearBatteryHealthFieldsAction,
    formClearPhevFields: formClearPhevFieldsAction,
    formCalculateNextReplacementDate: formCalculateNextReplacementDateAction,
    formCalculateNextReplacementDateInterval: formCalculateNextReplacementDateIntervalAction,
    formCalculateNextReplacementMileage: formCalculateNextReplacementMileageAction,
    formCalculateNextReplacementMileageInterval: formCalculateNextReplacementMileageIntervalAction,
    formCalculateServiceInspectionDate: formCalculateServiceInspectionDateAction,
    formCalculateServiceInspectionDateInterval: formCalculateServiceInspectionDateIntervalAction,
    formCalculateServiceInspectionMileage: formCalculateServiceInspectionMileageAction,
    formCalculateServiceInspectionMileageInterval:
      formCalculateServiceInspectionMileageIntervalAction,
    formCalculateFluidNextReplacementDateFromLastDate:
      formCalculateFluidNextReplacementDateFromLastDateAction,
    formCalculateFluidNextReplacementDateFromMonths:
      formCalculateFluidNextReplacementDateFromMonthsAction,
    formAutofillCo2Class: formAutofillCo2ClassAction,
    formResetOnTypeChange: formResetOnTypeChangeAction,
    assignVinErrorToContext: assign({
      vinDecodingError: ({event}) => {
        if (event.type === VEHICLE_FORM_EVENTS.VIN_DECODE_FAILED) {
          return event.error;
        }
        return undefined;
      },
    }),
    clearVinErrorFromContext: assign({
      vinDecodingError: undefined,
    }),
    assignDecodedVinDataToContext: assign({
      decodedVinData: ({event}) => {
        if (event.type === VEHICLE_FORM_EVENTS.VIN_DECODED_SUCCESS) {
          return event.decodedData;
        }
        return undefined;
      },
    }),
    assignVehicleVariantToContext: assign({
      vehicleVariant: ({event}) => {
        if (event.type === VEHICLE_FORM_EVENTS.VEHICLE_VARIANT_UPDATE) {
          return event.vehicleVariant;
        }
        return undefined;
      },
    }),
    assignVehicleIdToContext: assign({
      vehicleId: ({event}) => {
        if (event.type === VEHICLE_FORM_EVENTS.VEHICLE_ID_UPDATE) {
          return event.vehicleId;
        }
        return undefined;
      },
    }),
  },
  guards: {
    formIsReady: ({context}) => Boolean(context.form),
    canUpdateFromVin: ({context}) => Boolean(context.form && context.decodedVinData),
  },
}).createMachine({
  // XState Extension hash
  /** @xstate-layout N4IgpgJg5mDOIC5QDcwAsCWBjANmAYgPYBOAtgMQBqAogBICSAwgDLUD6lAggEr2cByAFTYBVAAoARToOoBtAAwBdRKAAOhWBgAuGQgDsVIAB6IAjAFYAnAA4AdABYAzPYvmATI-MA2eVYA0IACeiAC0ps628o7WHo5ulm7y8k4AvikBqJi4BCQUNAws7PQSopLSckqG6po6+oYmCKamAOzNtubNSV7WnpaOzeYBwY1uaRno2HhEZLYYEHjkAMqCPMIA6tKMDPwA4grKSCDV2roGhw2OPg7x3ub28d0DQ4j2rZGOlqbW9h1Rpm7mMYgTKTHIzOYLCTURgAeShHHo-H2VQ0Jzq50Ql3k10st3ulkegyCZn+jlsCVMfR8ngsXlG6WBE2y01Is3mYCoiLYUNhUJKixEjEY1EWi2Rh2OtTOoAuV3xeIe1iexIQzS89gcCUc8lxnUc-0BDJBzNybIWlC5PLh7HwnHorAk4rUqKl9UxcpuXjuiuVw3+91sLS+4QJ9m+0SBxqmpohHPw1GkIm4IrYnBEghh+HtzDYAqFIrFlQlLtOboQr2eCG6Gvkri8H3V4eskaZ0ZmEDAWEIEAweiglF7nP43Oh1v5guFoqdRxL6JliGa1nM5N8lnsYbc9y+1krlnkdjc2+aiT1BpbWTbrI7XZ7fYHeiHI95NrtDunktLGNVS5XVnXMS3awdxVUwdTsdd3HMUx7EbJxmyNVswVZZBewkTtuzARYAFcsCwOBYHId9Z2lYwzGsLxLEiCx5AGbpemiStQPkLxbG1RJ7nVOJ4lMc9QRZWwUL0NCb0wnC8NgAjZFMA5nRqT950acjKNrcwaO8HorAYkCwNsAFVJ+DpuK9XiTRmQThIw7DcPwwi3Bkmc5LnUjGg+ZctzidT6OA4Z1WXSwKSVQ1xgvJCBNQ9CO2oYhiBIR8rT5XMJwLIjHJIhp-h6WxmlAqD5ABD5mLcRjQNMWwvEXdT3EPTxHBMy8wqEiKwCimLiDi0d4Vte1qEdItZLRNKzGYtz3AGJVrD3OkiT9UwvFK75zA+Zo1wC+Dgr401zKalrYqMWAtAAQy0MBbAOgAzY7iAAClUpIAEpyCjUKtpEnbiBSgayz6NpoKiax90030huSMrsv8zpTwBOrQoAdyOrBMD7Qi+ocz6vwq381w3QDvMQVS3F0r0puh-i4a0BHeygTgsB0VByHi9gLSRFGPyc9KbGXCaklojS+lxlyYkiMN7CScbJvpdbTNZMmKb7anaY5JmnzHRL8ynFniLLL4KKo1Sea84qw3Jdwujy6rFpJ00ZcRqmaYwOmlYZtgurfDXUq1hJsW1Fxoi+A2QM3NzfHCUC3HN2qEJC0n4Zt+X7Y5ABZTgAGkbXoahmBKLYBB2HqPtdL8w5+d4fgsaIAbXYq+mNul1U+SxOjDS2Zmtym47phPrRzLMM6z2gc7zt20YUovsV8PL9T6f2-R1NpVP1D568btbGSjq2Y7bu26cEABNMQ097ths92Qf7NZwaECLto3H+nxPm1JdK+02tIlu6Cl+SFenuj8nY63jlGDJnKEfEQywYQJzYEnVO+d5LOVHg4HUHR1SzRUl4SsSo2icTVPYD+TdI4bRbhvOW-9yCAITDIEBYCIGdyhMwGBbNEBh08NcKCIty6P3sJWAYGoPADHfpSZezdpZENtgrcgYhuBwkFIIegMJhw7wTNwI+tBoTJ1VpOQsZ9NaF23O0OaQFQzhiBlWDoWUmz+QEZ-IRthW7ELEfGRMyZFipnTJmbM6jkpDwLiPQ8NY+iJGYrzLSwxvClUcPqNwddLF4MlvVWxoj45LAzGINgGxBBbERHsLxsD2ZQUDMtGi2Vhb9Gmow5aCD6zHhon8KGQI9AYXgIcb+uQUTuy-GERinwVxzUWkTY8jdrGxlacPZyFhSqhgGIeBukTvBFRVBjGC5EDGwQjPgqWthrzdkpveYZ3jnL1g1A3SpfQ1yLX+MVTc1xZoP0BkFVeBDkLhRElZcSjT+p7PSh0A8HQ4iAWMaBAY7RfBh3IouaIFs1n1RehhN6uyclDT3OSQ8qkxaFVKSMesukOZhxWoeIC1j4lwoYVWV4rE1w32yjqEplZbrtDrA2YpAwCUiPbmAIlF8pmUS9ASZiU9NL83+LiBBfMLENysWkFIQA */
  id: 'vehicleForm',

  context: {
    errors: [],
    form: undefined,
    vinDecodingError: undefined,
    decodedVinData: undefined,
    vehicleVariant: undefined,
    vehicleId: undefined,
    previousFuelType: null,
    previousHybridType: null,
    getCatalogMakesForType: undefined,
    getCatalogModelsForTypeAndMake: undefined,
  } as VehicleMachineContext,

  initial: 'idle',

  // Global event handlers - apply to all states
  on: {
    VEHICLE_VARIANT_UPDATE: {
      actions: 'assignVehicleVariantToContext',
    },
    VEHICLE_ID_UPDATE: {
      actions: 'assignVehicleIdToContext',
    },
  },

  states: {
    idle: {
      on: {
        START_WATCHING: {
          target: 'watching',
          actions: assign({
            form: ({event}) => event.form,
            // Initialize previous fuel type values from current form state
            previousFuelType: ({event}) => event.form.getValues().fuelType?.[0] ?? null,
            previousHybridType: ({event}) => event.form.getValues().hybridType?.[0] ?? null,
            getCatalogMakesForType: ({event}: {event: StartWatchingEvent}) =>
              event.getCatalogMakesForType,
            getCatalogModelsForTypeAndMake: ({event}: {event: StartWatchingEvent}) =>
              event.getCatalogModelsForTypeAndMake,
          }),
        },
        DECODE_VIN: {
          target: 'decodingVin',
        },
        VIN_DECODED_SUCCESS: {
          actions: 'assignDecodedVinDataToContext',
          target: 'vinDecodeSuccess',
        },
        VIN_DECODE_FAILED: {
          actions: 'assignVinErrorToContext',
          target: 'vinDecodeError',
        },
        FEATURES_AUTOFILL_SUCCESS: {
          guard: 'formIsReady',
          actions: {
            type: 'formUpdateFeatures',
            params: ({context, event}) => ({
              form: context.form!,
              features: event.features,
            }),
          },
        },
        PHOTOS_PRESET_SUCCESS: {
          guard: 'formIsReady',
          actions: {
            type: 'formUpdatePhotos',
            params: ({context, event}) => ({
              form: context.form!,
              photos: event.photos,
            }),
          },
        },
      },
    },

    decodingVin: {
      on: {
        VIN_DECODED_SUCCESS: {
          actions: 'assignDecodedVinDataToContext',
          target: 'vinDecodeSuccess',
        },
        VIN_DECODE_FAILED: {
          actions: 'assignVinErrorToContext',
          target: 'vinDecodeError',
        },
      },
    },

    vinDecodeSuccess: {
      entry: 'clearVinErrorFromContext',
      always: [
        {
          guard: 'canUpdateFromVin',
          actions: {
            type: 'formUpdateFromVin',
            params: ({context}) => ({
              formApi: context.form!,
              decodedData: context.decodedVinData,
            }),
          },
          target: 'watchingActive',
        },
        {
          guard: 'formIsReady',
          target: 'watchingActive',
        },
        {
          target: 'idle',
        },
      ],
    },

    vinDecodeError: {
      on: {
        VIN_DECODED_SUCCESS: {
          actions: 'assignDecodedVinDataToContext',
          target: 'vinDecodeSuccess',
        },
        VIN_DECODE_FAILED: {
          actions: 'assignVinErrorToContext',
        },
      },
      after: {
        [VIN_DECODE_ERROR_TIMEOUT_MS]: {target: 'idle'}, // Go back to idle after 5 seconds if no retry
      },
    },

    watching: {
      always: [
        {
          guard: 'formIsReady',
          target: 'watchingActive',
        },
      ],
    },

    watchingActive: {
      entry: assign({
        // Sync previous fuel type values with current form state when entering this state
        previousFuelType: ({context}) => context.form!.getValues().fuelType?.[0] ?? null,
        previousHybridType: ({context}) => context.form!.getValues().hybridType?.[0] ?? null,
      }),
      invoke: [
        {
          id: 'makeWatcher',
          src: 'makeWatcher',
          input: ({context}) => ({
            form: context.form!,
          }),
        },
        {
          id: 'modelWatcher',
          src: 'modelWatcher',
          input: ({context}) => ({
            form: context.form!,
          }),
        },
        {
          id: 'typeWatcher',
          src: 'typeWatcher',
          input: ({context}) => ({
            form: context.form!,
            getCatalogMakesForType: context.getCatalogMakesForType,
            getCatalogModelsForTypeAndMake: context.getCatalogModelsForTypeAndMake,
          }),
        },
        {
          id: 'fuelTypeWatcher',
          src: 'fuelTypeWatcher',
          input: ({context}) => ({
            form: context.form!,
          }),
        },
      ],

      on: {
        DECODE_VIN: {
          target: 'decodingVin',
        },
        VIN_DECODED_SUCCESS: {
          actions: 'assignDecodedVinDataToContext',
          target: 'vinDecodeSuccess',
        },
        VIN_DECODE_FAILED: {
          actions: 'assignVinErrorToContext',
          target: 'vinDecodeError',
        },
        MAKE_FIELD_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formReset',
            params: ({context}) => ({
              form: context.form!,
              fieldsToReset: [
                'modelFamily',
                'formModelFamily',
                'trim',
                'variant',
                'engineName',
                'engineCode',
                'power',
                'engineVolume',
              ],
            }),
          },
        },
        MODEL_FIELD_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formReset',
            params: ({context}) => ({
              form: context.form!,
              fieldsToReset: [
                'trim',
                'variant',
                'engineName',
                'engineCode',
                'power',
                'engineVolume',
              ],
            }),
          },
        },
        TYPE_FIELD_CHANGED: {
          guard: 'formIsReady',
          actions: [
            // Always reset bodyStyle — not shared across vehicle types
            {
              type: 'formReset',
              params: ({context}) => ({
                form: context.form!,
                fieldsToReset: ['bodyStyle'] as const,
              }),
            },
            // Conditionally reset make/model/title based on catalog lookup performed by typeWatcherActor.
            // Title is handled here (not above) so it is only cleared when make is also cleared.
            // When make is preserved, TitleField's own useEffect will update title from the
            // preserved make (and cleared model), so no manual title reset is needed.
            {
              type: 'formResetOnTypeChange',
              params: ({context, event}) => ({
                form: context.form!,
                makeExistsInNewType: event.makeExistsInNewType,
                modelExistsInNewType: event.modelExistsInNewType,
              }),
            },
          ],
        },
        FUEL_TYPE_CHANGED: {
          guard: 'formIsReady',
          actions: [
            {
              type: 'formClearPhevFields',
              params: ({context}) => ({
                form: context.form!,
                previousFuelType: context.previousFuelType,
                previousHybridType: context.previousHybridType,
              }),
            },
            {
              type: 'formClearBatteryHealthFields',
              params: ({context}) => ({
                form: context.form!,
                previousFuelType: context.previousFuelType,
              }),
            },
            assign({
              previousFuelType: ({context}) => {
                const values = context.form!.getValues();
                return values.fuelType?.[0] ?? null;
              },
              previousHybridType: ({context}) => {
                const values = context.form!.getValues();
                return values.hybridType?.[0] ?? null;
              },
            }),
          ],
        },
        CREATE_CUSTOM_MAKE: {
          guard: 'formIsReady',
          actions: [
            {
              type: 'formCreateCustomMake',
              params: ({context, event}) => ({
                form: context.form!,
                customMake: event.customMake,
              }),
            },
            {
              type: 'formReset',
              params: ({context}) => ({
                form: context.form!,
                fieldsToReset: [
                  'formModelFamily',
                  'modelFamily',
                  'customModelFamily',
                  'trim',
                  'variant',
                  'engineName',
                  'engineCode',
                  'power',
                  'engineVolume',
                ],
              }),
            },
          ],
        },
        CREATE_CUSTOM_MODEL: {
          guard: 'formIsReady',
          actions: [
            {
              type: 'formCreateCustomModel',
              params: ({context, event}) => ({
                form: context.form!,
                customModel: event.customModel,
              }),
            },
            {
              type: 'formReset',
              params: ({context}) => ({
                form: context.form!,
                fieldsToReset: [
                  'trim',
                  'variant',
                  'engineName',
                  'engineCode',
                  'power',
                  'engineVolume',
                ],
              }),
            },
          ],
        },
        PRODUCTION_YEAR_CHECK_SUCCESS: {
          guard: 'formIsReady',
          actions: {
            type: 'formUpdateProductionYear',
            params: ({context, event}) => ({
              form: context.form!,
              manufacturedOnYear: event.manufacturedOnYear,
              manufacturedOnMonth: event.manufacturedOnMonth,
            }),
          },
        },
        FEATURES_AUTOFILL_SUCCESS: {
          guard: 'formIsReady',
          actions: {
            type: 'formUpdateFeatures',
            params: ({context, event}) => ({
              form: context.form!,
              features: event.features,
            }),
          },
        },
        PHOTOS_PRESET_SUCCESS: {
          guard: 'formIsReady',
          actions: {
            type: 'formUpdatePhotos',
            params: ({context, event}) => ({
              form: context.form!,
              photos: event.photos,
            }),
          },
        },
        LAST_REPLACEMENT_DATE_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateNextReplacementDate',
            params: ({context, event}) => ({
              form: context.form!,
              name: event.name,
              value: event.value,
            }),
          },
        },
        LAST_REPLACEMENT_DATE_INTERVAL_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateNextReplacementDateInterval',
            params: ({context, event}) => ({
              form: context.form!,
              name: event.name,
              value: event.value,
            }),
          },
        },
        LAST_REPLACEMENT_MILEAGE_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateNextReplacementMileage',
            params: ({context, event}) => ({
              form: context.form!,
              name: event.name,
              value: event.value,
            }),
          },
        },
        LAST_REPLACEMENT_MILEAGE_INTERVAL_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateNextReplacementMileageInterval',
            params: ({context, event}) => ({
              form: context.form!,
              name: event.name,
              value: event.value,
            }),
          },
        },
        SERVICE_INSPECTIONS_DATE_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateServiceInspectionDate',
            params: ({context, event}) => ({
              form: context.form!,
              value: event.value,
            }),
          },
        },
        SERVICE_INSPECTIONS_DATE_INTERVAL_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateServiceInspectionDateInterval',
            params: ({context, event}) => ({
              form: context.form!,
              value: event.value,
            }),
          },
        },
        SERVICE_INSPECTIONS_MILEAGE_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateServiceInspectionMileage',
            params: ({context, event}) => ({
              form: context.form!,
              value: event.value,
            }),
          },
        },
        SERVICE_INSPECTIONS_MILEAGE_INTERVAL_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateServiceInspectionMileageInterval',
            params: ({context, event}) => ({
              form: context.form!,
              value: event.value,
            }),
          },
        },
        FLUID_REPLACEMENT_DATE_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateFluidNextReplacementDateFromLastDate',
            params: ({context, event}) => ({
              form: context.form!,
              name: event.name,
              value: event.value,
            }),
          },
        },
        FLUID_REPLACEMENT_MONTHS_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formCalculateFluidNextReplacementDateFromMonths',
            params: ({context, event}) => ({
              form: context.form!,
              name: event.name,
              value: event.value,
            }),
          },
        },
        CARBON_DIOXIDE_EMISSION_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formAutofillCo2Class',
            params: ({context, event}) => ({
              form: context.form!,
              emissionField: 'carbonDioxideEmission',
              classField: 'carbonDioxideEmissionClass',
              value: event.value,
            }),
          },
        },
        CARBON_DIOXIDE_EMISSION_DISCHARGED_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formAutofillCo2Class',
            params: ({context, event}) => ({
              form: context.form!,
              emissionField: 'carbonDioxideEmissionDischarged',
              classField: 'carbonDioxideEmissionClassDischarged',
              value: event.value,
            }),
          },
        },
        CARBON_DIOXIDE_EMISSION_WEIGHTED_COMBINED_CHANGED: {
          guard: 'formIsReady',
          actions: {
            type: 'formAutofillCo2Class',
            params: ({context, event}) => ({
              form: context.form!,
              emissionField: 'carbonDioxideEmissionWeightedCombined',
              classField: 'carbonDioxideEmissionClassWeightedCombined',
              value: event.value,
            }),
          },
        },
        STOP_WATCHING: {
          target: 'idle',
          actions: [
            sendTo('makeWatcher', {type: VEHICLE_FORM_EVENTS.STOP_WATCHING}),
            sendTo('modelWatcher', {type: VEHICLE_FORM_EVENTS.STOP_WATCHING}),
            sendTo('typeWatcher', {type: VEHICLE_FORM_EVENTS.STOP_WATCHING}),
            sendTo('fuelTypeWatcher', {type: VEHICLE_FORM_EVENTS.STOP_WATCHING}),
          ],
        },
      },
    },
  },
});
